import React, { useState, useEffect } from 'react';
import {
    Container,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    useTheme,
    Paper,
    CircularProgress,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    PhoneAndroid,
    ExpandMore,
    Image as ImageIcon,
    ColorLens,
    DesktopWindows,
    Opacity,
} from '@mui/icons-material';
import { PortalScreenHeader } from '../../../../components/PortalScreenHeader';
import { SplashLogoPicker } from './components/SplashLogoPicker';
import { TintColorPicker } from './components/TintColorPicker';
import { LogoTintColorPicker } from './components/LogoTintColorPicker';
import { PermissionWrapper } from '../../../../components/PermissionWrapper';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';
import apiClient from '../../../../services/api/ApiClient';
import { ApiEndpoints } from '../../../../config/constants';

interface SplashScreenSettings {
    splashLogo: string | null;
    splashTintColor: string | null;
    splashLogoTintColor: string | null;
    fallbackLogo: string | null;
    fallbackColor: string;
}

type PreviewMode = 'mobile' | 'desktop';

export const SplashScreen: React.FC = () => {
    const theme = useTheme();
    const { config, reloadConfig } = useStoreConfig();

    // Accordion state - multiple can be open
    const [expandedPanels, setExpandedPanels] = useState<string[]>(['logo']);
    
    // Preview mode state
    const [previewMode, setPreviewMode] = useState<PreviewMode>('mobile');

    // Settings state (actual saved values)
    const [settings, setSettings] = useState<SplashScreenSettings>({
        splashLogo: null,
        splashTintColor: null,
        splashLogoTintColor: null,
        fallbackLogo: config.appLogo || null,
        fallbackColor: config.primaryColor || '#800080'
    });
    
    // Preview state (unsaved changes for live preview)
    // undefined = no preview change, null = explicitly cleared, string = preview value
    const [previewLogo, setPreviewLogo] = useState<string | null>(null);
    const [previewTintColor, setPreviewTintColor] = useState<string | null>(null);
    const [previewLogoTintColor, setPreviewLogoTintColor] = useState<string | null | undefined>(undefined);
    
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load settings on mount
    useEffect(() => {
        const fetchSettings = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await apiClient.get<SplashScreenSettings>(
                    ApiEndpoints.SplashScreen.GetSettings
                );
                setSettings(response.data);
            } catch (err: any) {
                console.error('Failed to load splash screen settings:', err);
                setError(err.response?.data?.message || 'Failed to load splash screen settings');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, []);

    // Toggle accordion expansion
    const handlePanelChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanels(prev =>
            isExpanded
                ? [...prev, panel]
                : prev.filter(p => p !== panel)
        );
    };

    const handleLogoChange = async (newLogoUrl: string | null) => {
        setSettings(prev => ({
            ...prev,
            splashLogo: newLogoUrl
        }));
        // Clear preview state since save was successful
        setPreviewLogo(null);
        // Reload store config to update the actual splash screen
        await reloadConfig();
    };

    const handleColorChange = async (newColor: string | null) => {
        setSettings(prev => ({
            ...prev,
            splashTintColor: newColor
        }));
        // Clear preview state since save was successful
        setPreviewTintColor(null);
        // Reload store config to update the actual splash screen
        await reloadConfig();
    };

    const handleLogoTintColorChange = async (newColor: string | null) => {
        setSettings(prev => ({
            ...prev,
            splashLogoTintColor: newColor
        }));
        // Clear preview state since save was successful
        setPreviewLogoTintColor(undefined);
        // Reload store config to update the actual splash screen
        await reloadConfig();
    };

    // Live preview handlers (called immediately when adjusting settings)
    const handleLogoPreviewChange = (previewUrl: string | null) => {
        setPreviewLogo(previewUrl);
    };

    const handleTintColorPreviewChange = (previewColor: string) => {
        setPreviewTintColor(previewColor);
    };

    const handleLogoTintColorPreviewChange = (previewColor: string | null) => {
        setPreviewLogoTintColor(previewColor);
    };

    const handlePreviewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: PreviewMode | null) => {
        if (newMode) {
            setPreviewMode(newMode);
        }
    };

    const accordionSx = {
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        mb: 2,
        overflow: 'hidden',
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
    };

    const summarySx = {
        minHeight: { xs: 56, sm: 64 },
        '& .MuiAccordionSummary-content': {
            my: { xs: 1.5, sm: 2 },
        },
    };

    // Get effective values for preview (preview state takes priority for live updates)
    const effectiveLogo = previewLogo || settings.splashLogo || settings.fallbackLogo;
    const effectiveColor = previewTintColor || settings.splashTintColor || settings.fallbackColor;
    // undefined = no preview change (use saved), null or string = preview value
    const effectiveLogoTint = previewLogoTintColor !== undefined ? previewLogoTintColor : settings.splashLogoTintColor;
    
    // Check if there are unsaved changes being previewed
    const hasUnsavedPreviewChanges = previewLogo !== null || previewTintColor !== null || previewLogoTintColor !== undefined;

    if (isLoading) {
        return (
            <Container component='main' maxWidth='xl' sx={{ height: '100%', px: { xs: 1, md: 2 }, pb: 4 }}>
                <PortalScreenHeader
                    title='Loading Screen'
                    subtitle='Customize the logo and background color shown while your app loads'
                    icon={<PhoneAndroid />}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <PermissionWrapper screen='splash-screen' permission='view'>
            <Container
                component='main'
                maxWidth='xl'
                sx={{
                    height: '100%',
                    paddingX: { xs: 1, md: 2 },
                    paddingBottom: 4,
                }}
            >
                <PortalScreenHeader
                    title='Loading Screen'
                    subtitle='Customize the logo and background color shown while your app loads'
                    icon={<PhoneAndroid />}
                />

                {error && (
                    <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Live Preview */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{ fontFamily: 'primaryFont' }}
                            >
                                Live Preview
                            </Typography>
                            {hasUnsavedPreviewChanges && (
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        bgcolor: theme.palette.warning.main + '20',
                                        color: theme.palette.warning.main,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.warning.main,
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                            '@keyframes pulse': {
                                                '0%, 100%': { opacity: 1 },
                                                '50%': { opacity: 0.4 },
                                            },
                                        }}
                                    />
                                    UNSAVED
                                </Box>
                            )}
                        </Box>
                        <ToggleButtonGroup
                            value={previewMode}
                            exclusive
                            onChange={handlePreviewModeChange}
                            size='small'
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.7rem',
                                    textTransform: 'none',
                                }
                            }}
                        >
                            <ToggleButton value='mobile'>
                                <PhoneAndroid sx={{ fontSize: 16, mr: 0.5 }} />
                                Mobile
                            </ToggleButton>
                            <ToggleButton value='desktop'>
                                <DesktopWindows sx={{ fontSize: 16, mr: 0.5 }} />
                                Desktop
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    
                    {/* Mobile Preview */}
                    {previewMode === 'mobile' && (
                        <Paper
                            elevation={3}
                            sx={{
                                width: { xs: 180, sm: 220 },
                                height: { xs: 360, sm: 440 },
                                borderRadius: 4,
                                overflow: 'hidden',
                                mx: 'auto',
                                position: 'relative',
                                bgcolor: effectiveColor,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 8px 32px ${effectiveColor}40`,
                                border: `8px solid ${theme.palette.mode === 'dark' ? '#333' : '#222'}`,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {/* Notch */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 60,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: theme.palette.mode === 'dark' ? '#555' : '#333',
                                }}
                            />

                            {/* Logo */}
                            {effectiveLogo && (
                                <Box
                                    component='img'
                                    src={effectiveLogo.startsWith('http') ? effectiveLogo : undefined}
                                    alt='Splash Logo'
                                    sx={{
                                        width: { xs: 80, sm: 100 },
                                        height: { xs: 80, sm: 100 },
                                        objectFit: 'contain',
                                        borderRadius: '50%',
                                        ...(effectiveLogoTint && {
                                            filter: `drop-shadow(0 0 0 ${effectiveLogoTint})`,
                                            // Apply tint using CSS filter for preview
                                            WebkitFilter: 'none',
                                        }),
                                    }}
                                    style={effectiveLogoTint ? {
                                        // CSS-based tint approximation for preview
                                        filter: `brightness(0) saturate(100%) invert(${effectiveLogoTint === '#FFFFFF' ? '1' : '0'})`,
                                    } : undefined}
                                />
                            )}

                            {/* Loading Bar Preview */}
                            <Box
                                sx={{
                                    mt: 2,
                                    width: { xs: 100, sm: 120 },
                                    height: 3,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '40%',
                                        height: '100%',
                                        bgcolor: 'white',
                                        borderRadius: 1.5,
                                        animation: 'loading-slide 1.2s ease-in-out infinite',
                                        '@keyframes loading-slide': {
                                            '0%': { left: '-40%' },
                                            '100%': { left: '100%' },
                                        },
                                    }}
                                />
                            </Box>
                        </Paper>
                    )}

                    {/* Desktop Preview */}
                    {previewMode === 'desktop' && (
                        <Paper
                            elevation={3}
                            sx={{
                                width: { xs: '100%', sm: 600 },
                                maxWidth: '100%',
                                height: { xs: 280, sm: 360 },
                                borderRadius: 2,
                                overflow: 'hidden',
                                mx: 'auto',
                                position: 'relative',
                                bgcolor: effectiveColor,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 8px 32px ${effectiveColor}40`,
                                border: `4px solid ${theme.palette.mode === 'dark' ? '#333' : '#222'}`,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {/* Browser Chrome */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 28,
                                    bgcolor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#e8e8e8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    gap: 0.75,
                                }}
                            >
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f56' }} />
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#27ca41' }} />
                                <Box sx={{
                                    flex: 1,
                                    mx: 2,
                                    height: 16,
                                    bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fff',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <Typography
                                        sx={{
                                            fontSize: 9,
                                            color: theme.palette.text.disabled,
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {config.subdomain || 'your-store'}.cosmocart.store
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Content Area */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mt: 2,
                                }}
                            >
                                {/* Logo */}
                                {effectiveLogo && (
                                    <Box
                                        component='img'
                                        src={effectiveLogo.startsWith('http') ? effectiveLogo : undefined}
                                        alt='Splash Logo'
                                        sx={{
                                            width: { xs: 80, sm: 100 },
                                            height: { xs: 80, sm: 100 },
                                            objectFit: 'contain',
                                            borderRadius: '50%',
                                        }}
                                        style={effectiveLogoTint ? {
                                            filter: `brightness(0) saturate(100%) invert(${effectiveLogoTint === '#FFFFFF' ? '1' : '0'})`,
                                        } : undefined}
                                    />
                                )}

                                {/* Loading Bar Preview */}
                                <Box
                                    sx={{
                                        mt: 3,
                                        width: 200,
                                        height: 4,
                                        borderRadius: 2,
                                        bgcolor: 'rgba(255,255,255,0.3)',
                                        overflow: 'hidden',
                                        position: 'relative',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: '40%',
                                            height: '100%',
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            animation: 'loading-slide-desktop 1.2s ease-in-out infinite',
                                            '@keyframes loading-slide-desktop': {
                                                '0%': { left: '-40%' },
                                                '100%': { left: '100%' },
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Paper>
                    )}
                </Box>

                {/* Logo Picker Section */}
                <Accordion
                    expanded={expandedPanels.includes('logo')}
                    onChange={handlePanelChange('logo')}
                    sx={accordionSx}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={summarySx}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.primary.main + '15',
                                    color: theme.palette.primary.main,
                                }}
                            >
                                <ImageIcon fontSize='small' />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        fontSize: { xs: 14, sm: 16 }
                                    }}
                                >
                                    Splash Logo
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{
                                        display: { xs: 'none', sm: 'block' },
                                        fontFamily: 'primaryFont'
                                    }}
                                >
                                    Upload a custom logo for the splash screen
                                </Typography>
                            </Box>
                            {settings.splashLogo && (
                                <Box
                                    sx={{
                                        ml: 'auto',
                                        mr: 1,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.success.main + '20',
                                        color: theme.palette.success.main,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        display: { xs: 'none', sm: 'block' }
                                    }}
                                >
                                    CUSTOM LOGO SET
                                </Box>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <SplashLogoPicker
                            currentLogo={settings.splashLogo}
                            fallbackLogo={settings.fallbackLogo}
                            onLogoChange={handleLogoChange}
                            onPreviewChange={handleLogoPreviewChange}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Tint Color Section */}
                <Accordion
                    expanded={expandedPanels.includes('color')}
                    onChange={handlePanelChange('color')}
                    sx={accordionSx}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={summarySx}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.secondary.main + '15',
                                    color: theme.palette.secondary.main,
                                }}
                            >
                                <ColorLens fontSize='small' />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        fontSize: { xs: 14, sm: 16 }
                                    }}
                                >
                                    Background Color
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{
                                        display: { xs: 'none', sm: 'block' },
                                        fontFamily: 'primaryFont'
                                    }}
                                >
                                    Set a custom background/tint color for the splash screen
                                </Typography>
                            </Box>
                            {settings.splashTintColor && (
                                <Box
                                    sx={{
                                        ml: 'auto',
                                        mr: 1,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.success.main + '20',
                                        color: theme.palette.success.main,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        display: { xs: 'none', sm: 'block' }
                                    }}
                                >
                                    CUSTOM COLOR SET
                                </Box>
                            )}
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 0.5,
                                    bgcolor: effectiveColor,
                                    border: `2px solid ${theme.palette.divider}`,
                                    mr: 1,
                                }}
                            />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <TintColorPicker
                            currentColor={settings.splashTintColor}
                            fallbackColor={settings.fallbackColor}
                            onColorChange={handleColorChange}
                            onPreviewChange={handleTintColorPreviewChange}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Logo Tint Color Section */}
                <Accordion
                    expanded={expandedPanels.includes('logoTint')}
                    onChange={handlePanelChange('logoTint')}
                    sx={accordionSx}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={summarySx}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.info.main + '15',
                                    color: theme.palette.info.main,
                                }}
                            >
                                <Opacity fontSize='small' />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        fontSize: { xs: 14, sm: 16 }
                                    }}
                                >
                                    Logo Tint Color
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{
                                        display: { xs: 'none', sm: 'block' },
                                        fontFamily: 'primaryFont'
                                    }}
                                >
                                    Apply a color tint to your logo (useful for monochrome logos)
                                </Typography>
                            </Box>
                            {settings.splashLogoTintColor && (
                                <>
                                    <Box
                                        sx={{
                                            ml: 'auto',
                                            mr: 1,
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.success.main + '20',
                                            color: theme.palette.success.main,
                                            fontSize: 10,
                                            fontWeight: 600,
                                            fontFamily: 'primaryFont',
                                            display: { xs: 'none', sm: 'block' }
                                        }}
                                    >
                                        TINT APPLIED
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 0.5,
                                            bgcolor: settings.splashLogoTintColor,
                                            border: `2px solid ${theme.palette.divider}`,
                                            mr: 1,
                                        }}
                                    />
                                </>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <LogoTintColorPicker
                            currentColor={settings.splashLogoTintColor}
                            onColorChange={handleLogoTintColorChange}
                            onPreviewChange={handleLogoTintColorPreviewChange}
                        />
                    </AccordionDetails>
                </Accordion>
            </Container>
        </PermissionWrapper>
    );
};

export default SplashScreen;
